import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getLookupData } from 'modules/api/lookups';
import { Store } from 'components/common/types/Store.types';
import { getDealFormReadOnly } from 'store/dealManagement/selectors/getDealFormReadOnly';
import { getCurrentLineEnvironments } from 'store/dealManagement/selectors/getCurrentLineEnvironments';
import { getIsNewDeal } from 'store/dealManagement/selectors/getIsNewDeal';
import MultiSelect from 'lib/MultiSelect';
import Chip, { Color, Width } from 'lib/Chip';
import { setSelectedEnvironmentChannel, changeDealCurrentLineData, resetProperty } from 'store/dealManagement/reducer';
import { DealStatus, Environment as EnvironmentItem } from 'components/common/types/Deal.types';
import { getResellerDealMediaOwners } from 'store/reseller/selectors';
import { Label } from 'lib/Label/Label';

import { EnvironmentProps } from './Environment.types';

type EnvironmentListItem = EnvironmentItem & {
  disabled: boolean;
};

const Environment = ({ label, popoverPosition }: EnvironmentProps): React.ReactElement => {
  const dispatch = useDispatch();
  const resellerMediaOwners = useSelector(getResellerDealMediaOwners);
  const isNewDeal = useSelector(getIsNewDeal);
  const selectedEnvironmentChannel = useSelector((state: Store) => state.dealManagement.selectedEnvironmentChannel);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const environments = useSelector(getCurrentLineEnvironments, shallowEqual);

  const [availableEnvironments, setAvailableEnvironments] = useState<EnvironmentListItem[]>([]);

  const isPendingReservation = bookingStatusCode === DealStatus.PENDING_RESERVATION;
  const readOnly = useSelector(getDealFormReadOnly) && !isPendingReservation;

  const onAddEnvironment = (items: EnvironmentListItem[]): void => {
    if (items.length) {
      const { audienceCategoryGroupCode, dayPartGroupCode } = items[0];
      dispatch(setSelectedEnvironmentChannel({ audienceCategoryGroupCode, dayPartGroupCode }));
    } else {
      dispatch(resetProperty('selectedEnvironmentChannel'));
    }
    dispatch(changeDealCurrentLineData({ environments: items.map(({ disabled, ...item }) => item) }));
  };

  const onRemoveEnvironment = (value: EnvironmentListItem): void => {
    if (environments.length === 1 && isNewDeal) {
      dispatch(resetProperty('selectedEnvironmentChannel'));
    }
    const newEnvironments = environments.filter(({ code }) => code !== value.code);

    dispatch(changeDealCurrentLineData({ environments: newEnvironments }));
  };

  const onRemoveAllEnvironments = (): void => {
    dispatch(changeDealCurrentLineData({ environments: [] }));
  };

  const handleAvailableChannels = (): void => {
    if (environments.length && availableEnvironments.length) {
      const selectedEnv = availableEnvironments.find((env) => env.code === environments[0].code) || {};
      const { audienceCategoryGroupCode, dayPartGroupCode } = selectedEnv as EnvironmentItem;
      if (audienceCategoryGroupCode && dayPartGroupCode) {
        dispatch(setSelectedEnvironmentChannel({ audienceCategoryGroupCode, dayPartGroupCode }));
      }
    }
  };

  useEffect(() => {
    const environmentList = availableEnvironments.map((env) => {
      const disabled = selectedEnvironmentChannel
        ? !!(
            selectedEnvironmentChannel.audienceCategoryGroupCode !== env.audienceCategoryGroupCode ||
            selectedEnvironmentChannel.dayPartGroupCode !== env.dayPartGroupCode
          )
        : false;
      return { ...env, disabled };
    });
    setAvailableEnvironments(environmentList);
  }, [selectedEnvironmentChannel]);

  useEffect(() => {
    const getEnvironments = async (): Promise<void> => {
      try {
        const result = await getLookupData({ cancelFunctions: {}, lookupURLName: 'environment', query: '' });

        setAvailableEnvironments(result);
        handleAvailableChannels();
      } catch {
        setAvailableEnvironments([]);
      }
    };

    getEnvironments();
  }, [resellerMediaOwners]);

  useEffect(() => handleAvailableChannels(), [environments]);

  return (
    <div data-test-id="environment">
      {label && <Label label={label} isRequired />}
      <div className="flex flex-row">
        <div className="mr-3">
          <MultiSelect
            dataTestId="environment-input"
            items={availableEnvironments}
            selectedItems={environments}
            onSelect={onAddEnvironment}
            onClearAll={onRemoveAllEnvironments}
            isDisabled={readOnly}
            containerPosition={popoverPosition}
          />
        </div>
        {environments.length > 0 && (
          <div className="flex flex-wrap gap-x-7 gap-y-1" data-test-id="selected-environment">
            {environments.map((environment) => (
              <Chip
                key={environment.code}
                label={environment.name}
                isDisabled={readOnly}
                onRemove={() => onRemoveEnvironment({ ...environment, disabled: readOnly })}
                dataTestId={`selected-environment-${environment.code}`}
                color={Color.PRIMARY}
                width={Width.FULL}
                truncateLabel={false}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Environment;
